import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { ProgramModel } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { Dialog, DialogScroll } from "../primitives/Dialog";
import type { UiTheme } from "../theme";
import { makeModelSaveStyles } from "./modelSaveStyles";

/** What a save wrote, and what the model holds differently from its source. */
export type SavedModel = {
  name: string;
  queued: boolean;
  toHistory: string[];
  noLoad: string[];
  changed: { row: string; why: string[] }[];
  warmUps: number;
  model: Omit<ProgramModel, "id">;
};

// #354: the «Save as model» dialog after the save. Split out of
// ModelSaveDialog (file-size cap), same view.
export function ModelSavedView({
  theme,
  saved,
  onPlanFromModel,
  onClose,
}: {
  theme: UiTheme;
  saved: SavedModel;
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const styles = makeModelSaveStyles(theme);
  // A short title, one line of why, then hanging-indent rows: the bullet
  // sits in its own box so a wrapped line does not restart under it.
  const list = (title: string, why: string | null, rows: { row: string; why?: string[] }[]) =>
    rows.length === 0 ? null : (
      <View style={styles.group}>
        <Text style={styles.groupTitle}>{title}</Text>
        {why ? <Text style={styles.body}>{why}</Text> : null}
        {rows.map((r, i) => (
          <View key={`${i}-${r.row}`} style={styles.bulletRow}>
            <Text style={styles.body}>{"\u2022"}</Text>
            <View style={[styles.flex, styles.item]}>
              <Text style={styles.body}>{r.row}</Text>
              {(r.why ?? []).map((w) => (
                <Text key={w} style={styles.body}>
                  {w}
                </Text>
              ))}
            </View>
          </View>
        ))}
      </View>
    );

  const kept = saved.toHistory.length === 0 && saved.noLoad.length === 0 && saved.changed.length === 0;
  return (
    <Dialog
      theme={theme}
      visible
      onRequestClose={onClose}
      title={t("library.modelSavedTitle", { defaultValue: "Model saved" })}
    >
      <DialogScroll contentContainerStyle={styles.scroll}>
        <Text style={styles.body} accessibilityLiveRegion="polite" aria-live="polite">
          {t("library.modelSavedBody", {
            name: saved.name,
            defaultValue: "\"{{name}}\" is in New program, under My models.",
          })}
          {saved.queued
            ? ` ${t("library.modelQueued", { defaultValue: "It saves when you are back online." })}`
            : ""}
        </Text>
        {kept ? (
          <Text style={styles.body}>
            {t("library.modelAllKept", { defaultValue: "Every row kept its progression." })}
          </Text>
        ) : null}
        {saved.warmUps > 0 ? (
          <Text style={styles.body}>
            {t("library.modelWarmUps", {
              count: saved.warmUps,
              defaultValue: "{{count}} warm-up sets left out.",
            })}
          </Text>
        ) : null}
        {list(
          t("library.modelToHistory", { defaultValue: "Load from history" }),
          t("library.modelToHistoryWhy", {
            defaultValue: "No MAX, or added weight on a bodyweight move. It won't climb.",
          }),
          saved.toHistory.map((row) => ({ row })),
        )}
        {list(
          t("library.modelNoLoad", { defaultValue: "No load" }),
          t("library.modelNoLoadWhy", { defaultValue: "The weight is picked in the new program." }),
          saved.noLoad.map((row) => ({ row })),
        )}
        {list(
          t("library.modelChanged", { defaultValue: "Changed in the model" }),
          null,
          saved.changed,
        )}
      </DialogScroll>
      {/* Source order reads the main action first; row-reverse keeps Done on the left. */}
      <View style={[styles.actions, styles.actionsReverse]}>
        {onPlanFromModel ? (
          <Button
            theme={theme}
            label={t("library.modelPlan", { defaultValue: "Plan a program" })}
            numberOfLines={2}
            style={styles.actionButton}
            onPress={() => {
              onClose();
              onPlanFromModel(saved.model);
            }}
          />
        ) : null}
        <Button
          theme={theme}
          variant={onPlanFromModel ? "secondary" : "primary"}
          label={t("library.modelDone", { defaultValue: "Done" })}
          onPress={onClose}
          style={styles.actionButton}
        />
      </View>
    </Dialog>
  );
}
